import React from "react";
import ThemePageShell, { ThemeField, isValidHex } from "./ThemePageShell";
import { ThemePayload } from "../../context/ThemeContext";

const fields: ThemeField[] = [
  {
    name: "themeName",
    label: "Theme name",
    type: "text",
    placeholder: "Executive Suite",
    required: true,
    helper: "A clear theme identifier used in admin menus.",
  },
  {
    name: "primaryAccent",
    label: "Primary accent color",
    type: "color",
    placeholder: "#1A6CF6",
    required: true,
    helper: "Pick a strong accent for CTA buttons and headers.",
  },
  {
    name: "sidebarColor",
    label: "Sidebar background color",
    type: "color",
    placeholder: "#0F1729",
    required: true,
    helper: "Background color for the left navigation sidebar.",
  },
  {
    name: "sidebarTextColor",
    label: "Sidebar text color",
    type: "color",
    placeholder: "#94A3B8",
    required: true,
    helper: "Text and icon color for sidebar navigation items.",
  },
  {
    name: "topbarColor",
    label: "Top bar background color",
    type: "color",
    placeholder: "#0D1526",
    required: true,
    helper: "Background color for the top navigation bar.",
  },
  {
    name: "topbarTextColor",
    label: "Top bar text color",
    type: "color",
    placeholder: "#F1F5F9",
    required: true,
    helper: "Text color for the top navigation bar.",
  },
  {
    name: "layoutStyle",
    label: "Layout style",
    type: "select",
    placeholder: "Select layout",
    required: true,
    options: ["Dashboard first", "Panel-driven", "Split landing"],
    helper: "Choose the overall layout structure for this premium theme.",
  },
  {
    name: "ctaText",
    label: "Primary CTA label",
    type: "text",
    placeholder: "Start your premiere journey",
    required: true,
    helper: "Text for the main call-to-action button.",
  },
  {
    name: "securityMode",
    label: "Security mode",
    type: "select",
    placeholder: "Select mode",
    required: true,
    options: ["Strict", "Balanced", "Flexible"],
    helper:
      "Define the protective posture for the theme; strict mode locks down optional widgets.",
  },
  {
    name: "analyticsScope",
    label: "Analytics scope",
    type: "select",
    placeholder: "Select scope",
    required: true,
    options: [
      "Resident behavior",
      "Asset usage",
      "Event adoption",
      "Full stack",
    ],
    helper: "Choose which analytics modules are enabled for the theme.",
  },
];

const featureList = [
  "Executive dashboard with premium widgets",
  "High-contrast CTA and security badge accents",
  "Strict theme-level access mode",
  "Backend schema-ready payload fields",
];

const palette: ThemePayload = {
  sidebarBackground: "#0F1729",
  sidebarTextColor: "#94A3B8",
  topbarBackground: "#0D1526",
  topbarTextColor: "#F1F5F9",
  primaryAccent: "#1A6CF6",
  surface: "#F8FAFC",
};

const ThemeExecutiveSuite: React.FC = () => (
  <ThemePageShell
    title="Executive Suite Theme"
    subtitle="A premium management experience designed for committees, corporate societies and gated communities that demand stronger security, clear workflows and polished layouts."
    description="Complete this premium theme form to lock in the Executive Suite look and backend-safe configuration. Each field maps to a secure API request payload so your frontend remains declarative and protected."
    featureList={featureList}
    fields={fields}
    cta="Save Executive Theme"
    pathLabel="Premium Themes"
    themePayload={palette}
    baseSlug="executive-suite"
    liveFieldNames={["primaryAccent", "sidebarColor", "sidebarTextColor", "topbarColor", "topbarTextColor"]}
    getLiveOverrides={(values) => {
      const overrides: Partial<ThemePayload> = {};
      if (isValidHex(values.primaryAccent)) overrides.primaryAccent = values.primaryAccent;
      if (isValidHex(values.sidebarColor)) overrides.sidebarBackground = values.sidebarColor;
      if (isValidHex(values.sidebarTextColor)) overrides.sidebarTextColor = values.sidebarTextColor;
      if (isValidHex(values.topbarColor)) overrides.topbarBackground = values.topbarColor;
      if (isValidHex(values.topbarTextColor)) overrides.topbarTextColor = values.topbarTextColor;
      return overrides;
    }}
  />
);

export default ThemeExecutiveSuite;
